import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, EVENT_STATUS_META, errMsg } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CaretLeft, CaretRight, Gear, Plus, Trash, Check, Warning, Bed } from "@phosphor-icons/react";

const WD = ["S", "M", "T", "W", "T", "F", "S"];
const iso = (d) => d.toISOString().slice(0, 10);

function periodDays(view, cursor) {
  const c = new Date(cursor + "T00:00:00");
  if (view === "day") return [iso(c)];
  if (view === "week") {
    const start = new Date(c); start.setDate(c.getDate() - c.getDay());
    return Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return iso(d); });
  }
  // month
  const first = new Date(c.getFullYear(), c.getMonth(), 1);
  const last = new Date(c.getFullYear(), c.getMonth() + 1, 0);
  return Array.from({ length: last.getDate() }, (_, i) => iso(new Date(c.getFullYear(), c.getMonth(), i + 1)));
}

function shiftCursor(view, cursor, dir) {
  const c = new Date(cursor + "T00:00:00");
  if (view === "day") c.setDate(c.getDate() + dir);
  else if (view === "week") c.setDate(c.getDate() + dir * 7);
  else c.setMonth(c.getMonth() + dir);
  return iso(c);
}

function periodLabel(view, cursor) {
  const c = new Date(cursor + "T00:00:00");
  if (view === "month") return c.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  if (view === "day") return c.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric", year: "numeric" });
  const days = periodDays("week", cursor);
  const a = new Date(days[0] + "T00:00:00"), b = new Date(days[6] + "T00:00:00");
  return `${a.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${b.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
}

function ManageRoomsDialog({ open, onOpenChange, hotelId, rooms }) {
  const qc = useQueryClient();
  const [newName, setNewName] = useState("");
  const [newCap, setNewCap] = useState("");
  const invalidate = () => qc.invalidateQueries();
  const add = useMutation({
    mutationFn: () => api.createMeetingRoom({ hotel_id: hotelId, name: newName.trim(), capacity: newCap }),
    onSuccess: () => { invalidate(); setNewName(""); setNewCap(""); toast.success("Room added"); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const rename = useMutation({
    mutationFn: ({ id, name, capacity }) => api.updateMeetingRoom(id, { hotel_id: hotelId, name, capacity }),
    onSuccess: () => { invalidate(); toast.success("Room updated"); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const remove = useMutation({
    mutationFn: (id) => api.deleteMeetingRoom(id),
    onSuccess: () => { invalidate(); toast.success("Room removed"); },
    onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-[#111827] max-w-md" data-testid="manage-rooms-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Meeting rooms</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {rooms.map((r) => <RoomRow key={r.id} room={r} onRename={(name, capacity) => rename.mutate({ id: r.id, name, capacity })} onDelete={() => remove.mutate(r.id)} />)}
          {rooms.length === 0 && <p className="text-sm text-[#4B5563]">No rooms yet. Add your meeting spaces below.</p>}
        </div>
        <div className="border-t border-[#E5E7EB] pt-3 flex gap-2 items-end">
          <label className="text-sm flex-1"><span className="overline text-[#4B5563] block mb-1">New room name</span>
            <input data-testid="room-new-name" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Colorado Board Room" className="w-full border border-[#E5E7EB] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
          <label className="text-sm w-24"><span className="overline text-[#4B5563] block mb-1">Capacity</span>
            <input data-testid="room-new-cap" value={newCap} onChange={(e) => setNewCap(e.target.value)} placeholder="120" className="w-full border border-[#E5E7EB] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
          <button data-testid="room-add-btn" disabled={!newName.trim() || add.isPending} onClick={() => add.mutate()}
            className="bg-[#002FA7] text-white px-3 py-2 text-sm font-semibold hover:bg-[#00227A] disabled:opacity-60"><Plus size={15} weight="bold" /></button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RoomRow({ room, onRename, onDelete }) {
  const [name, setName] = useState(room.name);
  const [cap, setCap] = useState(room.capacity || "");
  const dirty = name !== room.name || cap !== (room.capacity || "");
  return (
    <div className="flex gap-2 items-center" data-testid={`room-row-${room.id}`}>
      <input data-testid={`room-name-${room.id}`} value={name} onChange={(e) => setName(e.target.value)} className="flex-1 border border-[#E5E7EB] px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
      <input value={cap} onChange={(e) => setCap(e.target.value)} placeholder="cap" className="w-16 border border-[#E5E7EB] px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
      {dirty && <button data-testid={`room-save-${room.id}`} onClick={() => onRename(name.trim(), cap)} className="text-[#047857]"><Check size={17} weight="bold" /></button>}
      <button data-testid={`room-del-${room.id}`} onClick={onDelete} className="text-[#4B5563] hover:text-[#DC2626]"><Trash size={15} weight="bold" /></button>
    </div>
  );
}

function RoomTypesDialog({ open, onOpenChange, hotelId, roomTypes }) {
  const qc = useQueryClient();
  const [newName, setNewName] = useState("");
  const [newInv, setNewInv] = useState("");
  const invalidate = () => qc.invalidateQueries();
  const add = useMutation({
    mutationFn: () => api.createRoomType({ hotel_id: hotelId, name: newName.trim(), inventory: Number(newInv) || 0 }),
    onSuccess: () => { invalidate(); setNewName(""); setNewInv(""); toast.success("Room type added"); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const rename = useMutation({
    mutationFn: ({ id, name, inventory }) => api.updateRoomType(id, { hotel_id: hotelId, name, inventory: Number(inventory) || 0 }),
    onSuccess: () => { invalidate(); toast.success("Updated"); }, onError: (e) => toast.error(errMsg(e)),
  });
  const remove = useMutation({
    mutationFn: (id) => api.deleteRoomType(id), onSuccess: () => { invalidate(); toast.success("Removed"); }, onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-[#111827] max-w-md" data-testid="room-types-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Guest room types</DialogTitle></DialogHeader>
        <p className="text-xs text-[#4B5563] -mt-2">Room types & inventory for this property. Used when building per-night room blocks on a lead.</p>
        <div className="space-y-2">
          {roomTypes.map((r) => <RoomTypeRow key={r.id} rt={r} onRename={(name, inventory) => rename.mutate({ id: r.id, name, inventory })} onDelete={() => remove.mutate(r.id)} />)}
          {roomTypes.length === 0 && <p className="text-sm text-[#4B5563]">No room types yet.</p>}
        </div>
        <div className="border-t border-[#E5E7EB] pt-3 flex gap-2 items-end">
          <label className="text-sm flex-1"><span className="overline text-[#4B5563] block mb-1">New room type</span>
            <input data-testid="rt-new-name" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="2 Queens Double" className="w-full border border-[#E5E7EB] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
          <label className="text-sm w-20"><span className="overline text-[#4B5563] block mb-1"># Rooms</span>
            <input data-testid="rt-new-inv" type="number" value={newInv} onChange={(e) => setNewInv(e.target.value)} placeholder="40" className="w-full border border-[#E5E7EB] px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
          <button data-testid="rt-add-btn" disabled={!newName.trim() || add.isPending} onClick={() => add.mutate()} className="bg-[#002FA7] text-white px-3 py-2 text-sm font-semibold hover:bg-[#00227A] disabled:opacity-60"><Plus size={15} weight="bold" /></button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RoomTypeRow({ rt, onRename, onDelete }) {
  const [name, setName] = useState(rt.name);
  const [inv, setInv] = useState(rt.inventory ?? "");
  const dirty = name !== rt.name || String(inv) !== String(rt.inventory ?? "");
  return (
    <div className="flex gap-2 items-center" data-testid={`rt-row-${rt.id}`}>
      <input data-testid={`rt-name-${rt.id}`} value={name} onChange={(e) => setName(e.target.value)} className="flex-1 border border-[#E5E7EB] px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
      <input value={inv} onChange={(e) => setInv(e.target.value)} type="number" className="w-16 border border-[#E5E7EB] px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
      {dirty && <button data-testid={`rt-save-${rt.id}`} onClick={() => onRename(name.trim(), inv)} className="text-[#047857]"><Check size={17} weight="bold" /></button>}
      <button data-testid={`rt-del-${rt.id}`} onClick={onDelete} className="text-[#4B5563] hover:text-[#DC2626]"><Trash size={15} weight="bold" /></button>
    </div>
  );
}

export default function FunctionDiary() {
  const { data: properties = [] } = useQuery({ queryKey: ["my-properties"], queryFn: () => api.myProperties() });
  const [hotelId, setHotelId] = useState("");
  const activeHotel = hotelId || properties[0]?.id || "";
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(iso(new Date()));
  const [manageOpen, setManageOpen] = useState(false);
  const [rtOpen, setRtOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ["function-diary", activeHotel], queryFn: () => api.functionDiary(activeHotel), enabled: !!activeHotel,
  });
  const { data: roomTypes = [] } = useQuery({
    queryKey: ["room-types", activeHotel], queryFn: () => api.roomTypes(activeHotel), enabled: !!activeHotel,
  });
  const rooms = data?.rooms || [];
  const functions = data?.functions || [];

  const days = useMemo(() => periodDays(view, cursor), [view, cursor]);

  // map: `${room_id||'unassigned'}|${date}` -> [functions]
  const cellMap = useMemo(() => {
    const m = {};
    functions.forEach((f) => {
      const key = `${f.room_id || "unassigned"}|${f.date}`;
      (m[key] = m[key] || []).push(f);
    });
    Object.values(m).forEach((arr) => arr.sort((a, b) => (a.start_time || "").localeCompare(b.start_time || "")));
    return m;
  }, [functions]);

  const hasUnassigned = functions.some((f) => !f.room_id);
  const rows = [...rooms, ...(hasUnassigned ? [{ id: "unassigned", name: "Unassigned", capacity: "" }] : [])];
  const colW = view === "day" ? 320 : view === "week" ? 150 : 96;

  return (
    <div className="space-y-4" data-testid="function-diary">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <button data-testid="diary-prev" onClick={() => setCursor(shiftCursor(view, cursor, -1))} className="border border-[#111827] p-2 hover:bg-[#F3F4F6]"><CaretLeft size={16} weight="bold" /></button>
          <button data-testid="diary-next" onClick={() => setCursor(shiftCursor(view, cursor, 1))} className="border border-[#111827] p-2 hover:bg-[#F3F4F6]"><CaretRight size={16} weight="bold" /></button>
          <button data-testid="diary-today" onClick={() => setCursor(iso(new Date()))} className="border border-[#111827] px-3 py-2 text-sm font-semibold hover:bg-[#F3F4F6]">Today</button>
          <span className="font-display font-bold text-lg text-[#111827] ml-2" data-testid="diary-label">{periodLabel(view, cursor)}</span>
        </div>
        <div className="flex items-center gap-2">
          {properties.length > 1 && (
            <select data-testid="diary-hotel" value={activeHotel} onChange={(e) => setHotelId(e.target.value)}
              className="border border-[#E5E7EB] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
              {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
          <div className="flex border border-[#111827]">
            {["day", "week", "month"].map((v) => (
              <button key={v} data-testid={`diary-view-${v}`} onClick={() => setView(v)}
                className={`px-3 py-2 text-sm font-semibold capitalize ${view === v ? "bg-[#111827] text-white" : "text-[#4B5563] hover:bg-[#F3F4F6]"}`}>{v}</button>
            ))}
          </div>
          <button data-testid="diary-manage-rooms" onClick={() => setManageOpen(true)}
            className="inline-flex items-center gap-1.5 border border-[#111827] px-3 py-2 text-sm font-semibold hover:bg-[#F3F4F6]"><Gear size={15} weight="bold" /> Rooms</button>
          <button data-testid="diary-room-types" onClick={() => setRtOpen(true)}
            className="inline-flex items-center gap-1.5 border border-[#111827] px-3 py-2 text-sm font-semibold hover:bg-[#F3F4F6]"><Bed size={15} weight="bold" /> Room Types</button>
        </div>
      </div>

      {rooms.length === 0 ? (
        <div className="border border-[#E5E7EB] p-8 text-center text-sm text-[#4B5563]" data-testid="diary-no-rooms">
          No meeting rooms yet. Click <b>Rooms</b> to add your meeting spaces.
        </div>
      ) : (
        <div className="border border-[#E5E7EB] overflow-x-auto" data-testid="diary-grid">
          <table className="border-collapse" style={{ minWidth: "100%" }}>
            <thead>
              <tr className="bg-[#F3F4F6]">
                <th className="sticky left-0 z-10 bg-[#F3F4F6] text-left px-3 py-2 text-xs font-semibold text-[#111827] border-r border-[#E5E7EB]" style={{ minWidth: 180 }}>Meeting Room</th>
                {days.map((d) => {
                  const dt = new Date(d + "T00:00:00");
                  const isToday = d === iso(new Date());
                  return (
                    <th key={d} className={`px-1 py-2 text-xs font-semibold border-r border-[#E5E7EB] ${isToday ? "bg-[#E0E7FF] text-[#002FA7]" : "text-[#4B5563]"}`} style={{ minWidth: colW }}>
                      {dt.getDate()} {WD[dt.getDay()]}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((room) => (
                <tr key={room.id} className="border-t border-[#E5E7EB]" data-testid={`diary-room-${room.id}`}>
                  <td className="sticky left-0 z-10 bg-white px-3 py-2 text-sm text-[#111827] border-r border-[#E5E7EB] align-top" style={{ minWidth: 180 }}>
                    {room.name}{room.capacity ? <span className="text-[#9CA3AF] text-xs"> · {room.capacity}</span> : null}
                  </td>
                  {days.map((d) => {
                    const items = cellMap[`${room.id}|${d}`] || [];
                    return (
                      <td key={d} className="px-1 py-1 border-r border-[#F3F4F6] align-top" style={{ minWidth: colW }}>
                        <div className="space-y-1">
                          {items.map((f) => {
                            const meta = EVENT_STATUS_META[f.status] || EVENT_STATUS_META.definite;
                            return (
                              <div key={f.id} data-testid={`diary-fn-${f.id}`} title={`${f.event_name} · ${f.start_time}–${f.end_time}${f.conflict ? " · DOUBLE-BOOKED" : ""}`}
                                className={`text-[10px] leading-tight text-white px-1 py-0.5 truncate ${f.conflict ? "ring-2 ring-[#DC2626]" : ""}`}
                                style={{ background: meta.color }}>
                                {f.conflict && <Warning size={9} weight="fill" className="inline mr-0.5" />}
                                <b>{f.start_time}</b> {view === "day" ? `${f.event_name} (${f.start_time}–${f.end_time})` : f.event_name}
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ManageRoomsDialog open={manageOpen} onOpenChange={setManageOpen} hotelId={activeHotel} rooms={rooms} />
      <RoomTypesDialog open={rtOpen} onOpenChange={setRtOpen} hotelId={activeHotel} roomTypes={roomTypes} />
    </div>
  );
}
